import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { collection, getCountFromServer, orderBy, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useCan } from "../lib/auth";
import { foldTr } from "../lib/searchKeys";
import { useCollection } from "../hooks/useCollection";
import { useProductionStageLabels, stageLabelOf } from "../hooks/useProductionStageLabels";
import { useCallableAction } from "../hooks/useCallableAction";
import type {
  SendOrdersToProductionInput,
  SendOrdersToProductionResult,
  SetOrderErpStatusInput,
  SetOrderErpStatusResult,
} from "../hooks/callableResults";
import { usePagedQuery } from "../hooks/usePagedQuery";
import { Pagination } from "../components/Pagination";
import { useToast } from "../components/Toast";
import { Modal } from "../components/Modal";

import { LoadingBlock, Spinner } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { tl, n, date } from "../lib/format";
import { paymentLabel, financialLabel, PAYMENT_TONE_CLASS } from "../lib/payment";
import { ChannelBadge, channelOf } from "../components/ChannelBadge";

/**
 * Sipariş listesi — eski `orders.php`nin karşılığı.
 *
 * KORUNAN DAVRANIŞ
 * · İki mod: parametresiz → statü kartları (adetli), `?status=` → kayıt listesi.
 * · Kart tıklanınca kalem detayı açılır/kapanır.
 * · İptal/iade statülerinde liste 25, diğerlerinde 100 kayıt (eski limit kuralı).
 * · Toplu "Statü Değiş" ve "Üretime Gönder".
 *
 * DÜZELTİLEN DAVRANIŞ
 * · Rol: `production` listeyi ve TUTARI görür (yetki matrisi §2.2) ama
 *   düzenleyemez. Eski `orders.php:7` üretimi tamamen dışlıyordu.
 * · Seçim: gizli checkbox + satır tıklama hilesi kaldırıldı; klavyeyle
 *   erişilebilir gerçek checkbox var. Satır tıklaması yalnızca detayı açar.
 * · Sonuç: "başarıyla güncellendi" sabit mesajı YOK. Callable ne dönerse o
 *   gösterilir; hata mesajı sunucudan Türkçe gelir ve aynen basılır.
 * · Negatif tutarlı sipariş GİZLENMEZ (eski sistem sessizce atlıyordu →
 *   sipariş kayıp görünüyordu); işaretlenerek gösterilir.
 */

const CALLABLE_SET_STATUS = "setOrderErpStatus";
const CALLABLE_SEND_TO_PRODUCTION = "sendOrdersToProduction";

/** Security Rules'ta sipariş kalemleri `orders/{id}/items` altındadır. */
/** Sipariş kalemleri. Shopify terminolojisi; indeksler de bu adla tanımlı. */
const ITEMS_SUBCOLLECTION = "lineItems";

interface StatusDoc {
  label?: string;
  category?: string;
  obsolete?: boolean;
  color?: string;
  sortOrder?: number;
  /** Eski şema adı — veri hangi adı taşıyorsa o kullanılır. */
  ordering?: number;
}

interface OrderDoc {
  /**
   * SATIŞ KANALI — "shopify" | "trendyol" | "legacy".
   * Eski (göç edilmiş) kayıtlarda alan yoktur; `channelOf()` o durumda
   * `shopifyOrderId` / `trendyol` alanlarından türetir.
   */
  channel?: string | null;
  trendyol?: { orderNumber?: string; status?: string } | null;
  shopifyOrderId?: string | null;

  orderName?: string;
  city?: string;
  financialStatus?: string;
  /**
   * ★ Bu iki alan KÖKTE YOK (0/14.613). Şehir `shippingAddress.city` içinde,
   * takip numarası `tracking.number` map'inde duruyor. Ekran kök alanları
   * okuduğu için şehir ve kargo rozeti hiçbir satırda görünmüyordu.
   */
  trackingNumber?: string;
  tracking?: { number?: string; company?: string };
  trackingInfo?: { trackingNumber?: string };
  shippingAddress?: { city?: string } | null;
  billingAddress?: { city?: string } | null;
  customerFullName?: string;
  paymentMethod?: string;
  shippingMethod?: string;
  totals?: { total?: number };
  createdAt?: { toDate: () => Date } | null;
  erpStatus?: string;
  erpStatusLabel?: string;
  productionId?: string;
  productionStatus?: string;
  lineItemCount?: number;
  totalQuantity?: number;
}

interface ItemDoc {
  title?: string;
  variantTitle?: string;
  optionSize?: string;
  sku?: string;
  quantity?: number;
  originalTotal?: number;
  discountedTotal?: number;
  imageUrl?: string;
  productId?: string;
  /** Katalogdaki gerçek doküman anahtarı (ürün ya da varyant kimliği). */
  catalogProductId?: string;
  isProductDeleted?: boolean;
}

/** Eski kural: iptal/iade listelerinde 25, diğerlerinde 100 kayıt. */
function statusOrder(s: StatusDoc): number {
  return s.sortOrder ?? s.ordering ?? 10000;
}

/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Siparişin kalemleri — açılır satırın içeriği.
 *
 * ★ DIŞA AÇILDI: Üretimi Bekleyen kuyruğu da aynı bileşeni kullanır. İkinci
 * bir kopya yazmak, bu projede defalarca "bir ekranda düzeltilip diğerinde
 * unutulan" davranışa yol açtı (kalem görseli, donmuş fiyat, silinmiş ürün
 * rozeti hepsi burada).
 */
export function OrderItems({ orderId }: { orderId: string }) {
  const items = useCollection<ItemDoc>(
    () => collection(db, "orders", orderId, ITEMS_SUBCOLLECTION),
    [orderId],
  );

  if (items.loading) return <LoadingBlock label="Kalemler yükleniyor" />;
  if (items.error) {
    return (
      <p role="alert" className="px-4 py-3 text-sm text-red-800">
        {items.error}
      </p>
    );
  }
  if (items.docs.length === 0) {
    return <p className="px-4 py-3 text-sm text-gray-500">Bu siparişte kalem kaydı yok.</p>;
  }

  return (
    <ul role="list" className="space-y-2 p-3">
      {items.docs.map((it) => (
        <li key={it.id} className="flex flex-wrap items-center gap-3 rounded-lg bg-gray-100 p-3">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded bg-white">
            {it.imageUrl ? (
              <img
                src={it.imageUrl}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-xs text-gray-600">görsel yok</span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">
              {it.productId && !it.isProductDeleted ? (
                <Link
                  to={`/products/${it.catalogProductId || it.productId}`}
                  className="link"
                >
                  {it.title ?? "Ürün"}
                </Link>
              ) : (
                <span>{it.title ?? "Ürün"}</span>
              )}
              {it.variantTitle ? <span className="text-gray-500"> · {it.variantTitle}</span> : null}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              {it.sku ? `SKU ${it.sku}` : "SKU yok"}
              {it.optionSize ? ` · ${it.optionSize}` : ""}
              {it.isProductDeleted ? " · üretimden kalkmış ürün" : ""}
            </p>
          </div>

          <div className="text-sm text-gray-700">{n(it.quantity, " adet")}</div>
          {/* Eski orders.php indirim ÖNCESİ tutarı (get_subtotal) gösteriyordu;
              üretim listesi ise indirimliyi. Burada ikisi de açıkça yazılıyor. */}
          <div className="w-28 text-right text-sm tabular-nums text-gray-900">
            {tl(it.discountedTotal ?? it.originalTotal)}
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Üretim rolüne GÖSTERİLMEYEN sipariş statüleri.
 *
 * Sevk edilmiş sipariş atölyenin işi değildir. Liste tek kalem: iş sahibi
 * yalnızca "Kargoya Verildi"yi işaret etti. Yeni bir aşama eklenecekse buraya
 * yazılır — koşul ekranın içine dağıtılmaz.
 */
const PRODUCTION_HIDDEN_STATUSES: readonly string[] = ["wc-kargoya-verildi"];

function StatusCards({ statuses }: { statuses: Array<StatusDoc & { id: string }> }) {
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [countError, setCountError] = useState<string | null>(null);
  const ids = statuses.map((s) => s.id).join(",");

  useEffect(() => {
    let cancelled = false;
    const list = ids ? ids.split(",") : [];
    if (list.length === 0) return;

    // Sayım toplama sorgusu (count) — dokümanları indirmeden adet verir.
    // Eski sistem tüm siparişleri çekip PHP'de sayıyordu.
    Promise.all(
      list.map(async (tech) => {
        const snap = await getCountFromServer(
          query(collection(db, "orders"), where("erpStatus", "==", tech)),
        );
        return [tech, snap.data().count] as const;
      }),
    )
      .then((pairs) => {
        if (cancelled) return;
        setCounts(Object.fromEntries(pairs));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        console.error("Statü sayımı başarısız", e);
        setCountError("Statü adetleri okunamadı. Liste yine de açılabilir.");
      });

    return () => {
      cancelled = true;
    };
  }, [ids]);

  return (
    <div className="space-y-3">
      {/* ★ TRENDYOL'DA WEBHOOK YOK — siparişler 10 dakikada bir POLL edilir.
          Bu düğme o taramayı elle ve GENİŞ pencereyle çalıştırır. Kurulumdan
          önceki siparişler hiçbir otomatik pencereye girmediği için gerekli.
          Trendyol yalnızca son 1 ayı verdiğinden üst sınır 30 gündür. */}
      <TrendyolPullButton />

      {countError ? (
        <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {countError}
        </p>
      ) : null}

      {/* ★ Statü kartları tek sütunlu dikey listeydi: 1280px genişlikte solda
          etiket, sağda adet, arada ~1000px boşluk. Uygulamanın en çok girilen
          açılış ekranıydı ve "ortası boş" şikayetinin doğrudan kaynağıydı. */}
      <ul role="list" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {statuses.map((s) => {
          const c = counts[s.id];
          return (
            <li key={s.id}>
              <Link
                to={`/orders?status=${encodeURIComponent(s.id)}`}
                className={`group flex min-h-[88px] flex-col justify-between gap-2 rounded-xl bg-white p-4 shadow-card ring-1 ring-gray-200/80 transition hover:-translate-y-0.5 hover:shadow-md hover:ring-primary-300 ${
                  c === 0 ? "opacity-60 hover:opacity-100" : ""
                }`}
              >
                <span className="flex items-center gap-2 text-sm font-medium text-gray-600 group-hover:text-gray-900">
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 shrink-0 rounded-full bg-gray-300"
                    style={s.color && s.color.startsWith("#") ? { backgroundColor: s.color } : undefined}
                  />
                  {s.label ?? s.id}
                </span>
                <span className="flex items-baseline gap-1.5">
                  {c === undefined ? (
                    <span className="h-7 w-12 animate-pulse rounded bg-gray-200/70" aria-label="sayılıyor" />
                  ) : (
                    <>
                      <span className="text-2xl font-bold tabular-nums tracking-tight text-gray-950">
                        {c === null ? "—" : c.toLocaleString("tr-TR")}
                      </span>
                      <span className="text-xs text-gray-500">kayıt</span>
                    </>
                  )}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

export function Orders() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const { isStaff } = useCan();
  const toast = useToast();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [targetStatus, setTargetStatus] = useState("");
  const [confirm, setConfirm] = useState<null | "status" | "production">(null);

  /**
   * ★ TİPLER ARTIK SUNUCUNUN GERÇEK ŞEKLİNDEN GELİYOR (`hooks/callableResults`).
   *
   * Burada elle yazılmış tipler duruyordu ve sunucuyla TUTMUYORDU:
   *   · `updated` / `message` alanları sunucuda HİÇ YOK.
   *   · `failed` DİZİ sanılıyordu; sunucu SAYI döndürüyor.
   * Tip uydurma olduğu için derleyici sessiz kaldı, hata ekrana "NaN siparişin
   * statüsü güncellendi" olarak düştü.
   */
  const setStatusAction = useCallableAction<SetOrderErpStatusInput, SetOrderErpStatusResult>(
    CALLABLE_SET_STATUS,
  );

  const sendAction = useCallableAction<
    SendOrdersToProductionInput,
    SendOrdersToProductionResult
  >(CALLABLE_SEND_TO_PRODUCTION);

  /**
   * Statü sözlüğü.
   * Tek `where` kullanılır; `obsolete` filtresi ve sıralama istemcide yapılır.
   * NEDEN: sıralama alanının adı veride `sortOrder` mı `ordering` mi olduğu
   * göçe bağlı; orderBy ile sorgulasaydık alanı olmayan dokümanlar sessizce
   * listeden düşerdi (Firestore'da alanı olmayan doküman orderBy sonucuna
   * girmez) — statü kartı kaybolurdu.
   */
  const statusesQ = useCollection<StatusDoc>(
    () => query(collection(db, "statuses"), where("category", "==", "orders")),
    [],
  );
  const stages = useProductionStageLabels();

  /**
   * ★ ÜRETİM ROLÜ SEVK SONRASINI GÖRMEZ.
   *
   * "Kargoya Verildi" atölyenin işi bittikten SONRAKİ aşamadır; iş sahibi
   * kararı: üretim aşamasındaki kişiyi ilgilendirmiyor, gösterilmesin.
   * Kart da liste de aynı süzgeçten geçer — kartı gizleyip listeyi açık
   * bırakmak, adresi elle yazan kullanıcıya kapıyı açık tutmak olurdu.
   *
   * ⚠ Bu bir GİZLİLİK sınırı değil, İLGİ sınırıdır: sipariş verisi Security
   * Rules'ta üretim rolüne zaten açık (`orders` → `isActive()`), çünkü atölye
   * kendi işinin siparişini okumak zorunda. Burada yapılan, işine yaramayan
   * aşamayı ekrandan çıkarmak.
   */
  const statuses = useMemo(
    () =>
      statusesQ.docs
        .filter((s) => s.obsolete !== true)
        .filter((s) => (isStaff ? true : !PRODUCTION_HIDDEN_STATUSES.includes(s.id)))
        .sort((a, b) => statusOrder(a) - statusOrder(b)),
    [statusesQ.docs, isStaff],
  );

  const current = statuses.find((s) => s.id === status);

  /**
   * ★ SABİT 100 SINIRI KALDIRILDI.
   * Liste `limit(100)` ile kesiliyordu ve sonraki sayfaya geçiş YOKTU: 100.
   * kayıttan sonrası uygulamadan erişilemiyordu. "Tamamlandı" statüsünde
   * 13.928 sipariş var — yani kayıtların %99'u görünmezdi.
   */
  const ordersQ = usePagedQuery<OrderDoc>(
    () =>
      status
        ? query(
            collection(db, "orders"),
            where("erpStatus", "==", status),
            orderBy("createdAt", "desc"),
          )
        : null,
    [status],
    50,
  );

  // Liste değişince seçim sıfırlanır — yanlış kayda toplu işlem yapılmasın.
  useEffect(() => {
    setSelected(new Set());
    setExpanded(new Set());
  }, [status]);

  useEffect(() => {
    if (!targetStatus && statuses.length > 0) setTargetStatus(status || statuses[0]!.id);
  }, [statuses, status, targetStatus]);

  /**
   * SAYFA İÇİ SÜZME — yüklenmiş satırlar üzerinde çalışır, sunucuya sormaz.
   *
   * `toLocaleLowerCase("tr-TR")` yeterli değildi: kullanıcı klavyeden "atas"
   * yazdığında "Ataş" satırı süzülüp KAYBOLUYORDU. Artık iki taraf da global
   * arama ile aynı katlamadan geçer.
   *
   * Kapsamın sayfa ile sınırlı olduğu kullanıcıya AÇIKÇA yazılır (aşağıdaki
   * ipucu satırı); "süzdüm, çıkmadı, demek ki yok" yanılgısı bu yüzden vardı.
   */
  const rows = useMemo(() => {
    const f = foldTr(filter.trim());
    if (!f) return ordersQ.docs;
    return ordersQ.docs.filter((o) =>
      foldTr([o.orderName ?? "", o.customerFullName ?? "", o.id].join(" ")).includes(f),
    );
  }, [ordersQ.docs, filter]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runStatusChange = async () => {
    const ids = [...selected];
    setConfirm(null);
    const out = await setStatusAction.run({ orderIds: ids, status: targetStatus });
    if (!out) {
      // Sunucudan gelen Türkçe HttpsError mesajı AYNEN gösterilir.
      toast.error(setStatusAction.error ?? "Statü değiştirilemedi.");
      return;
    }
    /**
     * ★ `out.failed` BİR SAYIDIR (`orders/status.ts:200,223`), dizi değil.
     *
     * Eski kod `out.failed ?? []` yazıp `.length` okuyordu: sayının `.length`'i
     * `undefined` olduğu için (1) `if (failed.length > 0)` HİÇBİR ZAMAN
     * tutmuyor, kısmi başarısızlıklar gösterilmiyor; (2) `ids.length -
     * undefined` **NaN** üretiyor ve ekranda "NaN siparişin statüsü
     * güncellendi" yazıyordu.
     *
     * Sebepler `results[]` içinde SATIR SATIR geliyor; sunucu onları zaten
     * Türkçe üretiyor (geçiş matrisi, yetki, silinmiş kayıt…). Artık
     * kullanıcıya HANGİ siparişin NİÇİN değişmediği yazılıyor.
     */
    const failedRows = out.results.filter((r) => !r.ok);
    const updated = out.results.length - out.failed;

    if (out.failed > 0) {
      toast.error(
        `${updated} sipariş güncellendi, ${out.failed} sipariş güncellenemedi.`,
        failedRows
          .map((r) => `${r.orderId}: ${r.error ?? "Sebep bildirilmedi."}`)
          .join("\n"),
      );
    } else {
      // Etiket de sunucudan gelir; ekran statü sözlüğünü ikinci kez okumasın.
      toast.success(`${updated} siparişin statüsü "${out.label}" olarak güncellendi.`);
    }
    setSelected(new Set());
  };

  const runSendToProduction = async () => {
    const ids = [...selected];
    setConfirm(null);
    const out = await sendAction.run({ orderIds: ids });
    if (!out) {
      toast.error(sendAction.error ?? "Üretime gönderilemedi.");
      return;
    }
    /**
     * ★ `skipped` ve `message` alanları SUNUCUDA YOK (`production/send.ts:303`
     * → `{ results, created }`). Ekran ikisini de okuyordu; `skipped` hep 0
     * kaldığı için "zaten üretimdeydi" satırı HİÇ çıkmadı ve üretime
     * gönderilemeyen siparişler tamamen sessiz kaldı — toplu işlemin yarısı
     * patlasa bile yeşil "gönderildi" bildirimi görünüyordu.
     *
     * Gerçek sayılar `results` dizisinden türetilir:
     *   · `created`            → sunucunun saydığı YENİ üretim kaydı sayısı.
     *   · ok && !created       → sipariş zaten üretimdeydi, yeniden açılmadı.
     *   · !ok                  → gönderilemedi; sebep satırın `error` alanında.
     */
    const failedRows = out.results.filter((r) => !r.ok);
    const alreadyInProduction = out.results.filter((r) => r.ok && !r.created).length;

    const skippedNote =
      alreadyInProduction > 0
        ? `${alreadyInProduction} sipariş zaten üretimdeydi, yeniden açılmadı.`
        : null;

    if (failedRows.length > 0) {
      toast.error(
        `${out.created} sipariş üretime gönderildi, ${failedRows.length} sipariş gönderilemedi.`,
        [
          ...(skippedNote ? [skippedNote] : []),
          ...failedRows.map((r) => `${r.orderId}: ${r.error ?? "Sebep bildirilmedi."}`),
        ].join("\n"),
      );
    } else {
      toast.success(`${out.created} sipariş üretime gönderildi.`, skippedNote ?? undefined);
    }
    setSelected(new Set());
  };

  const busy = setStatusAction.pending || sendAction.pending;

  return (
    <div className="space-y-5">
      {/* Başlık + kırıntı */}
      <div>
        <nav aria-label="Sayfa yolu" className="text-sm">
          <ol role="list" className="flex items-center gap-2 text-gray-500">
            <li>
              <Link to="/orders" className="hover:text-gray-700">
                Siparişler
              </Link>
            </li>
            {current ? (
              <>
                <li aria-hidden="true">/</li>
                <li className="text-gray-700">{current.label ?? current.id}</li>
              </>
            ) : null}
          </ol>
        </nav>

        <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-[28px] sm:leading-9">
            {current ? (current.label ?? current.id) : "Siparişler"}
          </h1>

          {status && isStaff ? (
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="target-status" className="sr-only">
                Yeni statü
              </label>
              <select
                id="target-status"
                className="select w-auto"
                value={targetStatus}
                onChange={(e) => setTargetStatus(e.target.value)}
                disabled={busy}
              >
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label ?? s.id}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn-danger"
                disabled={selected.size === 0 || !targetStatus || busy}
                onClick={() => setConfirm("status")}
              >
                Statü Değiş
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={selected.size === 0 || busy}
                onClick={() => setConfirm("production")}
              >
                Üretime Gönder
              </button>
              {busy ? <Spinner size="sm" label="İşleniyor" /> : null}
            </div>
          ) : null}
        </div>

        {status && !isStaff ? (
          <p className="mt-2 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">
            Bu liste sizin için salt okunurdur. Sipariş statüsünü ofis veya iş sahibi değiştirir.
          </p>
        ) : null}
      </div>

      {/* Statü sözlüğü hatası */}
      {statusesQ.error ? (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
          {statusesQ.error}
        </div>
      ) : null}

      {/* MOD A — statü kartları */}
      {!status ? (
        statusesQ.loading ? (
          <LoadingBlock />
        ) : statuses.length === 0 ? (
          <EmptyState
            title="Sipariş statüsü tanımlı değil"
            description="statuses koleksiyonunda 'orders' kategorisinde kayıt bulunamadı."
            tone="warning"
          />
        ) : (
          <StatusCards statuses={statuses} />
        )
      ) : null}

      {/* ★ GİZLİ STATÜYE ADRESTEN GİRİLEMEZ.
          Kartı gizleyip listeyi açık bırakmak, adresi elle yazan kullanıcıya
          kapıyı açık tutmak olurdu. Sebep AÇIKÇA yazılır — boş ekran "bozuk"
          gibi okunur. */}
      {status && !isStaff && PRODUCTION_HIDDEN_STATUSES.includes(status) ? (
        <div className="rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          <strong>Bu liste üretim rolüne kapalı.</strong> Kargoya verilmiş siparişler
          atölyenin işi bittikten sonraki aşamadır. Üretimdeki işleriniz için{" "}
          <Link to="/production" className="link">
            Üretim
          </Link>{" "}
          ekranını kullanın.
        </div>
      ) : null}

      {/* MOD B — sipariş listesi */}
      {status && !(!isStaff && PRODUCTION_HIDDEN_STATUSES.includes(status)) ? (
        <>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="w-full sm:max-w-xs">
              <label htmlFor="order-filter" className="sr-only">
                Listede ara
              </label>
              <input
                id="order-filter"
                type="search"
                className="input"
                placeholder="Bu sayfada süz (sipariş no, müşteri)"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              {filter.trim() ? (
                <p className="mt-1 text-xs text-gray-600">
                  Yalnızca yüklenmiş {ordersQ.docs.length} satır süzüldü, {rows.length} sonuç.
                  Tüm siparişlerde aramak için{" "}
                  <Link to={`/search?q=${encodeURIComponent(filter.trim())}`} className="font-semibold text-primary-700 underline">
                    genel aramayı
                  </Link>{" "}
                  kullanın.
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {/* ★ TOPLU SEÇİM. Tek tek işaretlemek dışında yolu yoktu; 50
                  siparişe statü uygulamak 50 tık demekti. Kutu bu SAYFAYI seçer
                  — sayfa dışını seçtiğini sanmak, görmediği kayda toplu işlem
                  yapmak olurdu. */}
              {isStaff && rows.length > 0 ? (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={rows.length > 0 && rows.every((o) => selected.has(o.id))}
                    ref={(el) => {
                      if (el) {
                        const some = rows.some((o) => selected.has(o.id));
                        el.indeterminate = some && !rows.every((o) => selected.has(o.id));
                      }
                    }}
                    onChange={(e) => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) rows.forEach((o) => next.add(o.id));
                        else rows.forEach((o) => next.delete(o.id));
                        return next;
                      });
                    }}
                  />
                  Bu sayfayı seç
                </label>
              ) : null}

              <p className="text-sm text-gray-700">
                {ordersQ.loading
                  ? "Yükleniyor…"
                  : `${rows.length} kayıt${filter ? " (aramaya uyan)" : ""}`}
                {selected.size > 0 ? ` · ${selected.size} seçili` : ""}
              </p>

              {selected.size > 0 ? (
                <button
                  type="button"
                  className="text-sm text-gray-600 underline-offset-2 hover:underline"
                  onClick={() => setSelected(new Set())}
                >
                  Seçimi temizle
                </button>
              ) : null}
            </div>
          </div>

          {ordersQ.loading ? (
            <LoadingBlock />
          ) : ordersQ.error ? (
            <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
              {ordersQ.error}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title="Bu statüde sipariş yok"
              description={filter ? "Arama filtresini temizlemeyi deneyin." : undefined}
            />
          ) : (
            <ul role="list" className="space-y-3">
              {rows.map((o) => {
                const isSelected = selected.has(o.id);
                const isOpen = expanded.has(o.id);
                const negative = (o.totals?.total ?? 0) < 0;
                return (
                  <li key={o.id}>
                    <div
                      className={`overflow-hidden rounded-lg bg-white shadow-sm ring-1 ${
                        isSelected ? "ring-2 ring-primary-700" : "ring-gray-200"
                      }`}
                    >
                      <div className="flex items-start gap-3 p-4">
                        {isStaff ? (
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600"
                            checked={isSelected}
                            onChange={() => toggleSelect(o.id)}
                            aria-label={`${o.orderName ?? o.id} siparişini seç`}
                          />
                        ) : null}

                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          aria-expanded={isOpen}
                          onClick={() => toggleExpand(o.id)}
                        >
                          {/* 1. satır: sipariş no + müşteri — listenin ana kimliği */}
                          <p className="flex flex-wrap items-center gap-x-2 text-sm">
                            {/* Kanal rozeti EN BAŞTA: Trendyol siparişinin
                                teslim penceresi dar, öncelik farkı ilk bakışta
                                görünmeli. */}
                            <ChannelBadge channel={channelOf(o)} />
                            <span className="font-mono text-xs text-gray-600">
                              {o.orderName ?? o.id}
                            </span>
                            <span className="font-semibold text-gray-900">
                              {o.customerFullName?.trim() || "İsimsiz müşteri"}
                            </span>
                            {(() => {
                              const city =
                                o.city ?? o.shippingAddress?.city ?? o.billingAddress?.city;
                              return city ? (
                                <span className="text-xs text-gray-600">· {city}</span>
                              ) : null;
                            })()}
                            {negative ? (
                              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold uppercase text-amber-900">
                                negatif tutar
                              </span>
                            ) : null}
                          </p>

                          {/* 2. satır: ödeme + kalem + üretim — hepsi rozet */}
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            {(() => {
                              const pm = paymentLabel(o.paymentMethod);
                              return (
                                <span
                                  className={`rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${PAYMENT_TONE_CLASS[pm.tone]}`}
                                  title={pm.instant ? "Peşin onaylı ödeme" : "Tahsilat onayı gerektirir"}
                                >
                                  {pm.label}
                                </span>
                              );
                            })()}

                            {o.financialStatus ? (
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700 ring-1 ring-inset ring-gray-200">
                                {financialLabel(o.financialStatus)}
                              </span>
                            ) : null}

                            {o.lineItemCount ? (
                              <span className="text-xs text-gray-500">
                                {o.lineItemCount} kalem
                                {o.totalQuantity ? ` · ${o.totalQuantity} adet` : ""}
                              </span>
                            ) : null}

                            {o.shippingMethod ? (
                              <span className="text-xs text-gray-500">· {o.shippingMethod}</span>
                            ) : null}

                            {o.productionStatus ? (
                              <span className="rounded bg-primary-50 px-1.5 py-0.5 text-xs font-medium text-primary-800 ring-1 ring-inset ring-primary-200">
                                {/* `productionStatusLabel` hiçbir yolda yazılmıyordu
                                    (0/4.400) — rozet ham "prod-cnc" basıyordu. */}
                                Üretim: {stageLabelOf(stages, o.productionStatus) ?? "…"}
                              </span>
                            ) : null}

                            {/* ★ TAKİP NUMARASI ÜÇ AYRI ADDA YAŞIYOR.
                                Kökteki `trackingNumber` canlı veride 0/14.619
                                dokümanda var; ERP kargo formu
                                `trackingInfo.trackingNumber`'a, Shopify kargo
                                webhook'u ve Trendyol taraması `tracking.number`'a
                                yazıyor. Ekran yalnızca kök alanı okuduğu için
                                kargo rozeti HİÇBİR satırda görünmüyordu.
                                Sunucuda yazma tek noktaya toplandı
                                (`orders/contract.ts`), burada da okuma zinciri
                                eski kayıtlar için yerinde bırakıldı. */}
                            {(() => {
                              const trackNo =
                                o.trackingInfo?.trackingNumber ??
                                o.tracking?.number ??
                                o.trackingNumber;
                              return trackNo ? (
                                <span className="rounded bg-cyan-50 px-1.5 py-0.5 text-xs text-cyan-800 ring-1 ring-inset ring-cyan-200">
                                  Kargo: {trackNo}
                                </span>
                              ) : null;
                            })()}
                          </div>
                        </button>

                        <div className="hidden shrink-0 flex-col items-end sm:flex">
                          {/* Tutar üç role de açıktır — gizlenen yalnızca maliyet/kâr. */}
                          <p className="text-sm tabular-nums text-gray-900">{tl(o.totals?.total)}</p>
                          <p className="mt-1 text-xs text-gray-500">{date(o.createdAt)}</p>
                        </div>

                        <Link
                          to={`/orders/${o.id}`}
                          className="shrink-0 rounded p-1 text-gray-600 hover:text-gray-700"
                          aria-label={`${o.orderName ?? o.id} sipariş detayını aç`}
                        >
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path
                              fillRule="evenodd"
                              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </Link>
                      </div>

                      {/* Dar ekranda tutar/tarih alt satıra iner */}
                      <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-xs text-gray-500 sm:hidden">
                        <span>{date(o.createdAt)}</span>
                        <span className="tabular-nums text-gray-900">{tl(o.totals?.total)}</span>
                      </div>

                      {isOpen ? (
                        <div className="border-t border-gray-100 bg-gray-50">
                          <OrderItems orderId={o.id} />
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Sayfalama: toplam sayı + aralık + sayfa boyutu + Önceki/Sonraki */}
          {rows.length > 0 || ordersQ.page > 1 ? (
            <Pagination
              page={ordersQ.page}
              pageSize={ordersQ.pageSize}
              total={ordersQ.total}
              shown={ordersQ.docs.length}
              hasNext={ordersQ.hasNext}
              hasPrev={ordersQ.hasPrev}
              onNext={ordersQ.next}
              onPrev={ordersQ.prev}
              onPageSize={ordersQ.setPageSize}
              loading={ordersQ.loading}
              className="mt-4"
            />
          ) : null}

          <div>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setParams({});
              }}
            >
              ← Tüm statüler
            </button>
          </div>
        </>
      ) : null}

      {/* Toplu işlem onayı — eski sistemde onay yoktu, yanlış tıklama geri alınamıyordu. */}
      <Modal
        open={confirm !== null}
        title={confirm === "production" ? "Üretime gönder" : "Statü değiştir"}
        description={
          confirm === "production"
            ? `${selected.size} sipariş için üretim kaydı açılacak. Zaten üretimde olanlar atlanır.`
            : `${selected.size} siparişin statüsü "${
                statuses.find((s) => s.id === targetStatus)?.label ?? targetStatus
              }" olarak değiştirilecek.`
        }
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setConfirm(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className={confirm === "production" ? "btn-primary" : "btn-danger"}
              onClick={() => {
                if (confirm === "production") void runSendToProduction();
                else void runStatusChange();
              }}
            >
              Onayla
            </button>
          </>
        }
      />
    </div>
  );
}

export default Orders;

/**
 * TRENDYOL'DAN ELLE ÇEKME.
 *
 * Zamanlanmış tarama son başarılı çalıştırmadan 15 dakika geriye bakar; ilk
 * kurulumda ise yalnızca 2 saat. Kurulumdan ÖNCE gelmiş siparişler bu yüzden
 * hiçbir pencereye girmez ve Balder'de görünmez. Bu düğme geçmişi tarar.
 *
 * Trendyol geçmişi SON 1 AY ile sınırlıdır ve bir istekte en fazla 2 hafta
 * kabul eder; sunucu pencereyi kendisi parçalar. Daha eskisi API'den ALINAMAZ —
 * bu bir hata değil, Trendyol'un sınırı.
 */
function TrendyolPullButton() {
  const { isStaff } = useCan();
  const toast = useToast();
  const pull = useCallableAction<
    { days: number },
    { scanned: number; created: number; updated: number; productionCreated: number; message?: string }
  >("syncTrendyolNow");

  if (!isStaff) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2">
      <ChannelBadge channel="trendyol" />
      <span className="text-sm text-orange-900">
        Trendyol siparişleri 10 dakikada bir otomatik çekilir. Shopify anında
        (webhook) düşer, ayrıca 10 dakikada bir kontrol edilir.
      </span>
      <button
        type="button"
        className="btn-secondary ml-auto"
        disabled={pull.loading}
        onClick={async () => {
          const res = await pull.run({ days: 30 });
          if (res) {
            toast.success(
              res.message ??
                `${res.scanned} paket tarandı · ${res.created} yeni · ${res.updated} güncellendi.`,
            );
          }
        }}
      >
        {pull.loading ? "Çekiliyor…" : "Son 30 günü şimdi çek"}
      </button>
      {pull.error ? (
        <p role="alert" className="w-full text-sm text-red-700">
          {pull.error}
        </p>
      ) : null}
    </div>
  );
}
